import clsx from 'clsx'
import type { KeyboardEvent } from 'react'

import type { CalendarBucketStatus, DayBucket } from './types'

const statusLabels: Record<CalendarBucketStatus, string> = {
  scheduled: 'scheduled',
  'in-transit': 'picked up',
  'out-for-delivery': 'out for delivery',
  delivered: 'delivered',
}

const chipClassName = (status: CalendarBucketStatus) =>
  clsx(
    'border text-left px-1 flex items-center gap-x-1 whitespace-nowrap fa-width-auto',
    status === 'delivered' && 'bg-fv-green-100 border-fv-green-300',
    status === 'out-for-delivery' && 'bg-fv-green-50 border-fv-green-200',
    status === 'in-transit' && 'bg-fv-beer-light border-fv-beer-medium',
    status === 'scheduled' && 'bg-fv-orange-50 border-fv-orange-300',
  )

export type CalendarDayItemProps = {
  dayOfMonth: number
  monthLabel: string
  isToday: boolean
  inCurrentMonth: boolean
  isSelected: boolean
  dataDateKey: string
  dayBuckets: DayBucket[]
  isKeyboardNavigating: boolean
  onSelect: () => void
  onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void
  onMouseEnter?: () => void
}

export const CalendarDayItem = ({
  dayOfMonth,
  monthLabel,
  isToday,
  inCurrentMonth,
  isSelected,
  dataDateKey,
  dayBuckets,
  isKeyboardNavigating,
  onSelect,
  onKeyDown,
  onMouseEnter,
}: CalendarDayItemProps) => {
  const hasBuckets = dayBuckets.length > 0
  const tabIndex = isSelected ? 0 : -1

  const backgroundClassName = isSelected
    ? 'bg-fv-beer-xlight'
    : !inCurrentMonth
      ? 'bg-[repeating-linear-gradient(135deg,rgba(255,255,255,0.5)_0_10px,rgba(254,242,242,0.9)_10px_20px)]'
      : 'bg-white'

  const borderClassName = clsx(
    !isKeyboardNavigating &&
      'hover:border-fv-gray-500 hover:border-solid hover:outline hover:outline-offset-[-2px] hover:outline-fv-gray-500 hover:z-20',
    'focus:outline focus:outline-offset-[-2px]',
    isSelected
      ? 'focus:border-fv-orange-bright focus:outline-fv-orange-bright'
      : 'focus:outline-fv-gray-500',
  )

  return (
    <button
      type="button"
      className={clsx(
        'border relative p-2 pt-8 flex flex-col items-start justify-start group',
        isSelected &&
          'border-fv-orange-bright outline outline-3 outline-offset-[-2px] outline-fv-orange-bright pointer-events-none',
        backgroundClassName,
        borderClassName,
      )}
      tabIndex={tabIndex}
      data-date-key={dataDateKey}
      onKeyDown={onKeyDown}
      onMouseEnter={onMouseEnter}
      onClick={onSelect}
    >
      {isToday && (
        <span className="absolute right-3 top-2 h-2 w-2 rounded-full bg-fv-orange-bright b1300:right-1" />
      )}
      <div
        className={clsx(
          'absolute border px-1 py-[.25rem] w-20 text-center flex items-center justify-center gap-1',
          !isKeyboardNavigating &&
            'group-hover:outline group-hover:outline-offset-[-2px] group-hover:outline-fv-gray-500 group-hover:border-fv-gray-500',
          isSelected
            ? 'bg-fv-beer-light border-fv-orange-bright -top-px -left-px outline outline-3 outline-offset-[-2px] outline-fv-orange-bright'
            : 'bg-fv-blue-25 border-fv-blue-200 -top-px -left-px',
        )}
      >
        <span className="leading-none">{dayOfMonth}</span>
        <span className="text-[0.55rem] uppercase whitespace-nowrap">
          {monthLabel}
        </span>
      </div>
      {isToday && !hasBuckets && (
        <div className="text-xs italic text-fv-gray-dark">
          Nothing for today
        </div>
      )}
      {hasBuckets && (
        <div className="text-xs flex gap-y-1 flex-col w-full">
          {dayBuckets.map(bucket => (
            <div key={bucket.status} className={chipClassName(bucket.status)}>
              <span className="truncate">
                {`${bucket.count} ${statusLabels[bucket.status]}`}
              </span>
            </div>
          ))}
        </div>
      )}
    </button>
  )
}
